import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';

import {
  ContractDocumentCardQuery,
  ContractDocumentsListQuery,
  ContractDocumentsListQueryVariables,
  useContractBoardColumnAmountsQuery,
  useContractDocumentCardQuery,
  useContractDocumentsListQuery,
} from 'core/graphql';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { contractAmountRows } from './contractsBoardModel';
import { isEsignEmailPending, isEsignInFlight } from './esignModel';
import { useEsignPermissions } from './useEsignPermissions';

export type IContractDocumentRow = ContractDocumentsListQuery['contractDocuments'][number];

export type IContractDocumentCard = NonNullable<
  ContractDocumentCardQuery['contractDocument']
>;

export type IContractDocumentsFilters = Omit<
  ContractDocumentsListQueryVariables,
  'limit' | 'offset'
>;

/** Опрос раз в 5 с — только пока на экране есть строка «в пути». */
export const ESIGN_POLL_MS = 5000;

const EMPTY_ROWS: IContractDocumentRow[] = [];

export interface IUseContractDocuments {
  rows: IContractDocumentRow[];
  total: number;
  /** Сколько договоров в каждом статусе при прочих фильтрах. */
  statusCounts: Record<string, number>;
  /**
   * ТЗ 0120: из них с шансом лида ≥ `hotMinWinChance` (только роли лидов;
   * переменная не передана — сервер берёт 0,7; не роли лидов — нули).
   */
  hotCounts: Record<string, number>;
  /** Есть выдача (текущая или прошлая, пока едет новая); нет — счётчики «—». */
  ready: boolean;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Список договоров с фильтрами и страницей (`limit`/`offset`).
 *
 * Страница — это ПЕРЕМЕННЫЕ запроса, а не `fetchMore`-склейка: опрос и
 * перезапрос перечитывают ровно текущую страницу и не схлопывают
 * накопленное (грабли `apollo-refetch-collapses-pages`).
 *
 * При смене фильтра данные для новых переменных ещё не пришли — таблица
 * держит прошлую выдачу (`previousData`), а не мигает пустотой: иначе
 * пустое состояние на полсекунды читается как «ничего не нашлось».
 *
 * `enabled=false` — вызывающий не имеет прав на домен (карточка, которую
 * открывает посторонняя роль): запрос не уходит вовсе.
 */
export const useContractDocuments = (
  filters: IContractDocumentsFilters,
  limit: number,
  offset: number,
  enabled = true,
  /**
   * ТЗ 0120: счётчики статусов. Колонки доски их не спрашивают — страница
   * берёт их одним запросом (`statusCounts`/`hotCounts` тогда пустые).
   */
  withCounts = true,
): IUseContractDocuments => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !enabled || !canOpenPage;
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentsListQuery({
    variables: { ...filters, limit, offset, withCounts },
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  // Прошлая выдача (другие фильтры) — только пока едет ответ на новые. Упал
  // ТЕКУЩИЙ запрос — её не выдаём за актуальную: пусто и ошибка «Повторить».
  const shown = data ?? (error ? undefined : previousData);
  const rows = shown?.contractDocuments ?? EMPTY_ROWS;
  const counts = shown?.contractDocumentStatusCounts;
  const statusCounts = useMemo(
    () =>
      Object.fromEntries((counts ?? []).map((row) => [row.status, row.count])),
    [counts],
  );
  const hotCounts = useMemo(
    () =>
      Object.fromEntries(
        (counts ?? []).map((row) => [row.status, row.hotCount ?? 0]),
      ),
    [counts],
  );
  const inFlight = rows.some(isEsignInFlight);

  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip && inFlight,
  );

  return {
    rows,
    total: shown?.contractDocumentsTotal ?? 0,
    statusCounts,
    hotCounts,
    ready: Boolean(shown),
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/**
 * ТЗ 0120, этап 2 — только ЧИСЛО договоров под фильтрами (мини-метрики
 * шапки и числа на быстрых чипах): тот же список с `limit: 1` (0 сервер
 * читает как «по умолчанию», 50 строк).
 * `null` — ответа ещё нет (или нет прав): «0» читался бы как «пусто».
 */
export const useContractDocumentsCount = (
  filters: IContractDocumentsFilters,
  enabled = true,
): number | null => {
  const { canOpenPage } = useEsignPermissions();
  const { data, previousData, error } = useContractDocumentsListQuery({
    variables: { ...filters, limit: 1, offset: 0, withCounts: false },
    skip: !enabled || !canOpenPage,
    fetchPolicy: 'cache-and-network',
  });
  const shown = data ?? (error ? undefined : previousData);
  return shown ? shown.contractDocumentsTotal : null;
};

/**
 * ТЗ 0120, этап 2 — точная сумма колонки доски по валютам (все договоры
 * колонки под фильтрами, а не только загруженные карточки). PLN первой,
 * без нулевых; `null` — ответа ещё нет.
 */
export const useContractColumnAmounts = (
  filters: IContractDocumentsFilters,
  enabled = true,
) => {
  const { canOpenPage } = useEsignPermissions();
  const variables = useMemo(() => {
    // Сортировка и порог «горячих» сумме не нужны (их нет в операции).
    const rest = { ...filters };
    delete rest.sort;
    delete rest.hotMinWinChance;
    return rest;
  }, [filters]);
  const { data, previousData, error } = useContractBoardColumnAmountsQuery({
    variables,
    skip: !enabled || !canOpenPage,
    fetchPolicy: 'cache-and-network',
  });
  const shown = data ?? (error ? undefined : previousData);
  const amounts = shown?.contractDocumentAmounts;
  return useMemo(() => (amounts ? contractAmountRows(amounts) : null), [
    amounts,
  ]);
};

export interface IUseContractDocument {
  contract: IContractDocumentCard | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * Карточка одного договора (ящик «Договор»).
 *
 * Опрашивается, пока идёт отправка или зачисление: оба процесса живут в
 * Celery, и без опроса человек смотрел бы на «Отправляется» до перезагрузки.
 */
export const useContractDocument = (
  id: string | null,
): IUseContractDocument => {
  const { canOpenPage } = useEsignPermissions();
  const skip = !id || !canOpenPage;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractDocumentCardQuery({
    variables: { id: id || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const contract = data?.contractDocument ?? null;
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    ESIGN_POLL_MS,
    !skip &&
      Boolean(
        contract &&
          (isEsignInFlight(contract) || isEsignEmailPending(contract)),
      ),
  );

  return {
    contract,
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};
